import { del, get, patch, post, postFormData, put, type RequestOptions } from '@/api/client';
import { invalidateFormTemplateEtagCache } from '@/api/etagCache';
import {
  adaptTriggerParserFromWire,
  normalizeFormBlocksResponse,
  normalizeFormConditionsResponse,
  serializeTriggerParserToWire,
  type FormTriggerParserWire,
} from './formsWireUtils';
import type {
  FormAiAssistInput,
  FormAiAssistResponse,
  FormAssignInput,
  FormAssignmentsResponse,
  FormAssignResponse,
  FormBlock,
  FormBlockCatalogResponse,
  FormBlockWrite,
  FormBlocksReorderPayload,
  FormBlocksResponse,
  FormCondition,
  FormConditionWrite,
  FormConditionsResponse,
  FormDocumentResponse,
  FormForkResponse,
  FormLookupResponse,
  FormLookupSource,
  FormPackageRecord,
  FormPackageWrite,
  FormPackagesResponse,
  FormPatientLinkResponse,
  FormQueueResponse,
  FormApproveInput,
  FormMarkIncompleteInput,
  FormResendInput,
  FormStatsResponse,
  FormSubmission,
  FormSubmissionDetail,
  FormSubmissionCreateInput,
  FormSubmissionWrite,
  FormTemplateCreateResponse,
  FormTemplateDetail,
  FormTemplatesLegacyResponse,
  FormTemplatesResponse,
  FormThemePresetsResponse,
  FormThemeRecord,
  FormThemesResponse,
  FormTriggerConfirmResult,
  FormTriggerDisplayRule,
  FormTriggerParseResult,
  FormTriggerParserConfirmInput,
  FormTriggerParserParseInput,
  FormTriggerParserState,
  FormTriggerRule,
  FormTriggerRuleWrite,
  FormTriggerRulesResponse,
  FormValidateResponse,
  SendFormInput,
  SendFormResponse,
} from './types';

export const formsApi = {
  // ── Templates ──────────────────────────────────────────────────────────────
  getTemplatesList: (options?: RequestOptions) =>
    get<FormTemplatesResponse>('/api/forms/list', undefined, options),

  getTemplatesSimple: (options?: RequestOptions) =>
    get<FormTemplatesLegacyResponse>('/api/forms/templates', undefined, options),

  getStats: (options?: RequestOptions) => get<FormStatsResponse>('/api/forms/stats', undefined, options),

  getTemplate: (formId: string, options?: RequestOptions) =>
    get<FormTemplateDetail>(`/api/forms/templates/${formId}`, undefined, options),

  createTemplate: (data: { name: string; description?: string }) =>
    post<FormTemplateCreateResponse>('/api/forms/templates', data),

  updateTemplate: (formId: string, data: Partial<FormTemplateDetail>) =>
    patch<FormTemplateDetail>(`/api/forms/templates/${formId}`, data),

  deleteTemplate: (formId: string) => del<void>(`/api/forms/templates/${formId}`),

  forkTemplate: (formId: string) => post<FormForkResponse>(`/api/forms/templates/${formId}/fork`),

  validateTemplate: (formId: string) =>
    post<FormValidateResponse>(`/api/forms/templates/${formId}/validate`),

  aiAssist: (formId: string, data: FormAiAssistInput) =>
    post<FormAiAssistResponse>(`/api/forms/templates/${formId}/ai-assist`, data),

  // ── Block catalog & lookups ────────────────────────────────────────────────
  getBlockCatalog: (options?: RequestOptions) =>
    get<FormBlockCatalogResponse>('/api/forms/block-catalog', undefined, options),

  lookup: (source: FormLookupSource, q: string, options?: RequestOptions) =>
    get<FormLookupResponse>(`/api/forms/lookup/${source}`, { q }, options),

  // ── Blocks ─────────────────────────────────────────────────────────────────
  getBlocks: async (formId: string, options?: RequestOptions) => {
    const data = await get<FormBlocksResponse | FormBlock[]>(
      `/api/forms/templates/${formId}/blocks`,
      undefined,
      options,
    );
    return { blocks: normalizeFormBlocksResponse(data) };
  },

  createBlock: async (formId: string, data: FormBlockWrite) => {
    const created = await post<FormBlock>(`/api/forms/templates/${formId}/blocks`, data);
    invalidateFormTemplateEtagCache(formId);
    return created;
  },

  patchBlock: async (formId: string, blockId: string, data: Partial<FormBlockWrite>) => {
    const updated = await patch<FormBlock>(
      `/api/forms/templates/${formId}/blocks/${blockId}`,
      data
    );
    invalidateFormTemplateEtagCache(formId);
    return updated;
  },

  deleteBlock: async (formId: string, blockId: string) => {
    await del<void>(`/api/forms/templates/${formId}/blocks/${blockId}`);
    invalidateFormTemplateEtagCache(formId);
  },

  reorderBlocks: async (formId: string, data: FormBlocksReorderPayload) => {
    await post<void>(`/api/forms/templates/${formId}/blocks/reorder`, data);
    invalidateFormTemplateEtagCache(formId);
  },

  // ── Conditions ─────────────────────────────────────────────────────────────
  getConditions: async (formId: string, options?: RequestOptions) => {
    const data = await get<FormConditionsResponse | FormCondition[]>(
      `/api/forms/templates/${formId}/conditions`,
      undefined,
      options,
    );
    return { conditions: normalizeFormConditionsResponse(data) };
  },

  createCondition: (formId: string, data: FormConditionWrite) =>
    post<FormCondition>(`/api/forms/templates/${formId}/conditions`, data),

  patchCondition: (formId: string, conditionId: string, data: Partial<FormConditionWrite>) =>
    patch<FormCondition>(`/api/forms/templates/${formId}/conditions/${conditionId}`, data),

  deleteCondition: (formId: string, conditionId: string) =>
    del<void>(`/api/forms/templates/${formId}/conditions/${conditionId}`),

  // ── Trigger rules ──────────────────────────────────────────────────────────
  getTriggerRules: (formId: string, options?: RequestOptions) =>
    get<FormTriggerRulesResponse>(
      `/api/forms/templates/${formId}/trigger_rules`,
      undefined,
      options,
    ),

  createTriggerRule: (formId: string, data: FormTriggerRuleWrite) =>
    post<FormTriggerRule>(`/api/forms/templates/${formId}/trigger_rules`, data),

  patchTriggerRule: (formId: string, ruleId: string, data: Partial<FormTriggerRuleWrite>) =>
    patch<FormTriggerRule>(`/api/forms/templates/${formId}/trigger_rules/${ruleId}`, data),

  deleteTriggerRule: (formId: string, ruleId: string) =>
    del<void>(`/api/forms/templates/${formId}/trigger_rules/${ruleId}`),

  // ── Trigger parser ─────────────────────────────────────────────────────────
  getTriggerParser: async (formId: string, options?: RequestOptions) => {
    const data = await get<FormTriggerParserWire>(
      `/api/forms/templates/${formId}/trigger-parser`,
      undefined,
      options,
    );
    return adaptTriggerParserFromWire(data);
  },

  putTriggerParser: async (formId: string, data: FormTriggerParserState) => {
    const wire = serializeTriggerParserToWire(data.raw_text ?? '');
    const response = await put<FormTriggerParserWire>(
      `/api/forms/templates/${formId}/trigger-parser`,
      wire
    );
    return adaptTriggerParserFromWire(response);
  },

  parseTriggerParser: async (
    formId: string,
    data: FormTriggerParserParseInput,
  ): Promise<FormTriggerParseResult> => {
    const response = await post<{ parse_result?: FormTriggerParseResult }>(
      `/api/forms/templates/${formId}/trigger-parser/parse`,
      data,
    );
    const result = response.parse_result;
    return {
      description_text: result?.description_text ?? '',
      preview_hash: result?.preview_hash ?? '',
      unsupported: result?.unsupported ?? true,
      rules: result?.rules ?? [],
      ...(result?.count != null ? { count: result.count } : {}),
      ...(result?.parser_version != null ? { parser_version: result.parser_version } : {}),
    };
  },

  confirmTriggerParser: async (
    formId: string,
    data: FormTriggerParserConfirmInput,
  ): Promise<FormTriggerConfirmResult> => {
    const response = await post<{ confirm_result?: FormTriggerConfirmResult }>(
      `/api/forms/templates/${formId}/trigger-parser/confirm`,
      data,
    );
    return {
      confirmed_at: response.confirm_result?.confirmed_at ?? '',
      description_text: response.confirm_result?.description_text ?? '',
      count: response.confirm_result?.count ?? 0,
    };
  },

  createTriggerDisplayRule: (formId: string, data: Record<string, unknown>) =>
    post<FormTriggerDisplayRule>(`/api/forms/templates/${formId}/trigger-display-rules`, data),

  patchTriggerDisplayRule: (formId: string, ruleId: string, data: Record<string, unknown>) =>
    patch<FormTriggerDisplayRule>(
      `/api/forms/templates/${formId}/trigger-display-rules/${ruleId}`,
      data
    ),

  deleteTriggerDisplayRule: (formId: string, ruleId: string) =>
    del<void>(`/api/forms/templates/${formId}/trigger-display-rules/${ruleId}`),

  // ── Assign & send ──────────────────────────────────────────────────────────
  assignTemplate: (formId: string, data: FormAssignInput) =>
    post<FormAssignResponse>(`/api/forms/templates/${formId}/assign`, data),

  sendForm: (data: SendFormInput) => post<SendFormResponse>('/api/forms/send', data),

  mintPatientLink: (assignmentId: string) =>
    post<FormPatientLinkResponse>(`/api/forms/assignments/${assignmentId}/patient-link`),

  // ── Approval queue & submissions ─────────────────────────────────────────────
  getQueue: (options?: RequestOptions) => get<FormQueueResponse>('/api/forms/queue', undefined, options),

  /** `GET /api/forms/assignments` — requires `forms_admin`; location-scoped. */
  getAssignments: (
    params?: { locationId?: number; status?: string; q?: string },
    options?: RequestOptions,
  ) =>
    get<FormAssignmentsResponse>(
      '/api/forms/assignments',
      {
        ...(params?.locationId != null ? { location_id: params.locationId } : {}),
        ...(params?.status ? { status: params.status } : {}),
        ...(params?.q ? { q: params.q } : {}),
      },
      options,
    ),

  getSubmission: (submissionId: string, options?: RequestOptions) =>
    get<FormSubmissionDetail>(`/api/forms/submissions/${submissionId}`, undefined, options),

  createSubmission: (data: FormSubmissionCreateInput) =>
    post<FormSubmission>('/api/forms/submissions', data),

  patchSubmission: (submissionId: string, data: FormSubmissionWrite) =>
    patch<FormSubmission>(`/api/forms/submissions/${submissionId}`, data),

  submitSubmission: (submissionId: string, data: FormSubmissionWrite) =>
    post<FormSubmission>(`/api/forms/submissions/${submissionId}/submit`, data),

  approveSubmission: (submissionId: string, data: FormApproveInput) => {
    const formData = new FormData();
    formData.append('pdf_file', data.pdf_file, 'submission.pdf');
    if (data.acknowledged_plan_version != null) {
      formData.append('acknowledged_plan_version', String(data.acknowledged_plan_version));
    }
    if (data.notes) formData.append('notes', data.notes);
    return postFormData<FormSubmission>(`/api/forms/submissions/${submissionId}/approve`, formData);
  },

  /** Assignment-scoped: closes a `pending`/`in_progress`/`submitted` assignment (D25/D28). */
  markIncompleteAssignment: (assignmentId: string, data?: FormMarkIncompleteInput) =>
    post<FormSubmission>(`/api/forms/assignments/${assignmentId}/mark-incomplete`, data),

  /** Recall redelivers the existing link on the chosen channel; only valid on a `submitted` submission. */
  resendSubmission: (submissionId: string, data: FormResendInput) =>
    post<FormSubmission>(`/api/forms/submissions/${submissionId}/resend`, data),

  /**
   * Redeliver the link for a form that was sent but not opened yet (no submission).
   * Same body as submission resend: `{ channel: 'sms' | 'email' }`.
   */
  resendAssignment: (assignmentId: string, data: FormResendInput) =>
    post<{ id: string; status: string }>(`/api/forms/assignments/${assignmentId}/resend`, data),

  getSubmissionDocument: (submissionId: string, options?: RequestOptions) =>
    get<FormDocumentResponse>(
      `/api/forms/submissions/${submissionId}/document`,
      undefined,
      options,
    ),

  retrySubmissionDocument: (submissionId: string) =>
    post<FormDocumentResponse>(`/api/forms/submissions/${submissionId}/document/retry`),

  // ── Packages ───────────────────────────────────────────────────────────────
  getPackages: (options?: RequestOptions) =>
    get<FormPackagesResponse | FormPackageRecord[]>('/api/forms/packages', undefined, options),

  createPackage: (data: FormPackageWrite) => post<FormPackageRecord>('/api/forms/packages', data),

  patchPackage: (packageId: string, data: Partial<FormPackageWrite>) =>
    patch<FormPackageRecord>(`/api/forms/packages/${packageId}`, data),

  deletePackage: (packageId: string) => del<void>(`/api/forms/packages/${packageId}`),

  // ── Themes ─────────────────────────────────────────────────────────────────
  getThemePresets: (options?: RequestOptions) =>
    get<FormThemePresetsResponse>('/api/forms/themes/presets', undefined, options),

  getThemes: (options?: RequestOptions) => get<FormThemesResponse>('/api/forms/themes', undefined, options),

  createTheme: (data: Partial<FormThemeRecord>) => post<FormThemeRecord>('/api/forms/themes', data),

  patchTheme: (themeId: string, data: Partial<FormThemeRecord>) =>
    patch<FormThemeRecord>(`/api/forms/themes/${themeId}`, data),

  deleteTheme: (themeId: string) => del<void>(`/api/forms/themes/${themeId}`),
};

/** Backward-compatible alias used by existing hooks. */
export const getTemplates = formsApi.getTemplatesList;
